import React, { useState } from 'react';
import adminAttendanceService from '../../firebase/adminAttendanceService';

/**
 * AttendanceRecordCard Component
 * Displays individual attendance record with photo thumbnail and actions
 */
const AttendanceRecordCard = ({ record, onStatusUpdate, onPhotoView, onRefresh }) => {
  const [updating, setUpdating] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const handleStatusUpdate = async (newStatus) => {
    if (updating) return;

    try {
      setUpdating(true);
      const result = await adminAttendanceService.updateAttendanceStatus(record.id, newStatus);
      
      if (result.success) {
        onStatusUpdate && onStatusUpdate(record.id, newStatus);
        onRefresh && onRefresh();
      } else {
        alert(`Failed to update status: ${result.error}`);
      }
    } catch (error) {
      console.error('Error updating attendance status:', error);
      alert('Failed to update attendance status');
    } finally {
      setUpdating(false);
    }
  };

  const handlePhotoClick = async () => {
    if (!record.photoUrl) {
      showToast('❌ No photo available for this attendance record.', 'warning');
      return;
    }

    let loadingToast = null;
    const loadingId = `loading_${Date.now()}`;

    try {
      // Show enhanced loading state
      loadingToast = showLoadingToast('📷 Loading photo...', loadingId);

      // Use enhanced fallback method
      const photoUrl = await adminAttendanceService.getPhotoWithFallback(record.id, record.photoUrl);
      
      // Remove loading toast
      removeToast(loadingId);

      if (photoUrl) {
        onPhotoView && onPhotoView(photoUrl, record);
        showToast('✅ Photo loaded successfully', 'success', 2000);
      } else {
        throw new Error('No accessible photo URL returned');
      }
    } catch (error) {
      // Remove loading toast if it exists
      removeToast(loadingId);
      
      console.error('❌ Photo loading error:', error);
      await handlePhotoError(error);
    }
  };


  const handlePhotoError = async (error) => {
    // Use enhanced error messages from the service
    const userMessage = error.userMessage || 'Unable to load photo due to an unexpected error.';
    const actionable = error.actionable || 'Please try again or contact support.';

    // Show detailed error information in development
    if (process.env.NODE_ENV === 'development') {
      console.error('🔍 Detailed photo error:', {
        code: error.code,
        message: error.message,
        userMessage: userMessage,
        actionable: actionable,
        attendanceId: error.attendanceId,
        retryCount: error.retryCount,
        clientRequestId: error.clientRequestId,
        serverRequestId: error.serverRequestId,
        timestamp: error.timestamp
      });
    }

    // Determine toast type based on error
    let toastType = 'error';
    if (error.code === 'functions/permission-denied') {
      toastType = 'warning';
    } else if (error.code === 'functions/deadline-exceeded' || error.message?.includes('timeout')) {
      toastType = 'warning';
    }

    // Show user-friendly error message
    showToast(`${userMessage} ${actionable}`, toastType, 6000);

    // Log error for monitoring (in production, this could send to analytics)
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'photo_load_error', {
        error_code: error.code,
        attendance_id: error.attendanceId,
        retry_count: error.retryCount
      });
    }
  };

  const showToast = (message, type = 'info', duration = 5000) => {
    const toastId = `toast_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    const toast = document.createElement('div');
    toast.id = toastId;
    toast.textContent = message;
    
    const colors = {
      success: '#52c41a',
      error: '#ff4d4f',
      warning: '#faad14',
      info: '#1890ff'
    };

    const icons = {
      success: '✅',
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️'
    };

    toast.style.cssText = `
      position: fixed; top: 20px; right: 20px; z-index: 10000;
      background: ${colors[type]}; color: white; padding: 12px 20px;
      border-radius: 8px; font-size: 14px; box-shadow: 0 4px 16px rgba(0,0,0,0.2);
      max-width: 450px; word-wrap: break-word; line-height: 1.4;
      border-left: 4px solid rgba(255,255,255,0.3);
      animation: slideInRight 0.3s ease-out;
    `;

    // Add CSS animation
    if (!document.getElementById('toast-animations')) {
      const style = document.createElement('style');
      style.id = 'toast-animations';
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }
    
    // Add icon to message
    toast.textContent = `${icons[type]} ${message}`;
    
    document.body.appendChild(toast);
    
    // Auto remove after specified duration
    setTimeout(() => {
      removeToast(toastId);
    }, duration);

    return toastId;
  };

  const showLoadingToast = (message, loadingId) => {
    const toast = document.createElement('div');
    toast.id = loadingId;
    
    toast.innerHTML = `
      <div style="display: flex; align-items: center; gap: 10px;">
        <div style="width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3); border-top: 2px solid white; border-radius: 50%; animation: spin 1s linear infinite;"></div>
        <span>${message}</span>
      </div>
    `;
    
    toast.style.cssText = `
      position: fixed; top: 20px; right: 20px; z-index: 10000;
      background: #1890ff; color: white; padding: 12px 20px;
      border-radius: 8px; font-size: 14px; box-shadow: 0 4px 16px rgba(0,0,0,0.2);
      max-width: 400px; word-wrap: break-word;
      border-left: 4px solid rgba(255,255,255,0.3);
    `;

    // Add spinner animation
    if (!document.getElementById('spinner-animation')) {
      const style = document.createElement('style');
      style.id = 'spinner-animation';
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
    
    document.body.appendChild(toast);
    return loadingId;
  };

  const removeToast = (toastId) => {
    const toast = document.getElementById(toastId);
    if (toast) {
      toast.style.animation = 'slideOutRight 0.3s ease-in';
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 300);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatLocation = (location) => {
    if (!location) return 'N/A';
    return `${location.latitude?.toFixed(6) || 'N/A'}, ${location.longitude?.toFixed(6) || 'N/A'}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#52c41a';
      case 'pending': return '#faad14';
      case 'rejected': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return '✅';
      case 'pending': return '⏳';
      case 'rejected': return '❌';
      default: return '❓';
    }
  };

  const getTypeIcon = (type) => {
    return type === 'clock-in' ? '🟢' : '🔴';
  };

  const getLocationIcon = (isWarehouse) => {
    return isWarehouse ? '🏢' : '🌍';
  };

  const getValidationScoreColor = (score) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#faad14';
    return '#ff4d4f';
  };

  return (
    <div className="attendance-record-card">
      {/* Card Header */}
      <div className="record-header">
        <div className="record-basic-info">
          <div className="employee-info">
            <h4 className="employee-name">{record.employeeName || 'Unknown Employee'}</h4>
            <span className="employee-id">ID: {record.employeeId || record.uid}</span>
          </div>
          
          <div className="record-meta">
            <div className="record-type">
              <span className="type-icon">{getTypeIcon(record.type)}</span>
              <span className="type-text">{record.type === 'clock-in' ? 'Clock In' : 'Clock Out'}</span>
            </div>
            
            <div className="record-location">
              <span className="location-icon">{getLocationIcon(record.isWarehouse)}</span>
              <span className="location-text">{record.isWarehouse ? 'Warehouse' : 'Job-site'}</span>
            </div>
          </div>
        </div>

        <div className="record-status-section">
          <div className="record-status">
            <span 
              className="status-badge"
              style={{ 
                backgroundColor: getStatusColor(record.status),
                color: 'white'
              }}
            >
              {getStatusIcon(record.status)} {record.status?.toUpperCase() || 'UNKNOWN'}
            </span>
          </div>
          
          <div className="record-timestamp">
            {formatDate(record.timestamp)}
          </div>
        </div>
      </div>

      {/* Photo Thumbnail */}
      <div className="record-photo-section">
        {record.photoUrl ? (
          <div className="photo-thumbnail-container">
            <img
              src={record.photoUrl.startsWith('gs://') ? 
                `https://storage.googleapis.com/${record.photoUrl.replace('gs://', '')}` : 
                record.photoUrl}
              alt="Attendance Photo"
              className="photo-thumbnail"
              onClick={handlePhotoClick}
              loading="lazy"
              onError={(e) => {
                // Hide the broken image and show clickable icon instead
                e.target.style.display = 'none';
                const container = e.target.parentElement;
                if (container && !container.querySelector('.photo-error-icon')) {
                  const errorIcon = document.createElement('div');
                  errorIcon.className = 'photo-error-icon';
                  errorIcon.innerHTML = '📷';
                  errorIcon.title = 'Click to view photo';
                  errorIcon.onclick = handlePhotoClick;
                  container.appendChild(errorIcon);
                }
              }}
            />
            <div className="photo-overlay" onClick={handlePhotoClick}>
              <span className="photo-overlay-text">📷 View Photo</span>
            </div>
          </div>
        ) : (
          <div className="no-photo clickable-photo-icon" onClick={handlePhotoClick} title="Click to view photo">
            <span className="no-photo-icon">📷</span>
            <span className="no-photo-text">Click to View Photo</span>
          </div>
        )}
      </div>

      {/* Validation Score */}
      <div className="validation-section">
        <div className="validation-score">
          <span className="validation-label">Validation Score:</span>
          <span 
            className="validation-value"
            style={{ color: getValidationScoreColor(record.validationResults?.overallScore || 0) }}
          >
            {record.validationResults?.overallScore || 0}%
          </span>
        </div>
        
        {record.validationResults && (
          <div className="validation-details">
            <div className="validation-item">
              <span className={`validation-check ${record.validationResults.photoRecency ? 'pass' : 'fail'}`}>
                {record.validationResults.photoRecency ? '✅' : '❌'} Photo Recency
              </span>
            </div>
            <div className="validation-item">
              <span className={`validation-check ${record.validationResults.gpsAccuracy ? 'pass' : 'fail'}`}>
                {record.validationResults.gpsAccuracy ? '✅' : '❌'} GPS Accuracy
              </span>
            </div>
            <div className="validation-item">
              <span className={`validation-check ${record.validationResults.warehouseProximity ? 'pass' : 'fail'}`}>
                {record.validationResults.warehouseProximity ? '✅' : '❌'} Location
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="record-actions">
        <div className="status-actions">
          {record.status !== 'approved' && (
            <button
              className="action-btn approve-btn"
              onClick={() => handleStatusUpdate('approved')}
              disabled={updating}
            >
              {updating ? '⏳' : '✅'} Approve
            </button>
          )}
          
          {record.status !== 'rejected' && (
            <button
              className="action-btn reject-btn"
              onClick={() => handleStatusUpdate('rejected')}
              disabled={updating}
            >
              {updating ? '⏳' : '❌'} Reject
            </button>
          )}
          
          {record.status !== 'pending' && (
            <button
              className="action-btn pending-btn"
              onClick={() => handleStatusUpdate('pending')}
              disabled={updating}
            >
              {updating ? '⏳' : '⏳'} Pending
            </button>
          )}
        </div>

        <button
          className="details-toggle-btn"
          onClick={() => setShowDetails(!showDetails)}
        >
          {showDetails ? '▼ Hide Details' : '▶ Show Details'}
        </button>
      </div>

      {/* Detailed Information (Collapsible) */}
      {showDetails && (
        <div className="record-details">
          <div className="details-grid">
            <div className="detail-item">
              <label>GPS Coordinates:</label>
              <span>{formatLocation(record.location)}</span>
            </div>
            
            <div className="detail-item">
              <label>GPS Accuracy:</label>
              <span>{record.location?.accuracy ? `${record.location.accuracy}m` : 'N/A'}</span>
            </div>
            
            <div className="detail-item">
              <label>Altitude:</label>
              <span>{record.location?.altitude ? `${record.location.altitude}m` : 'N/A'}</span>
            </div>
            
            {record.nearestWarehouse && (
              <div className="detail-item">
                <label>Nearest Warehouse:</label>
                <span>{record.nearestWarehouse.name} ({record.nearestWarehouse.distanceKm}km)</span>
              </div>
            )}
            
            <div className="detail-item">
              <label>Device Info:</label>
              <span>{record.photoMetadata?.deviceInfo ? 
                `${record.photoMetadata.deviceInfo.platform || 'Unknown'} - ${record.photoMetadata.deviceInfo.userAgent?.substring(0, 50) || 'Unknown'}...` : 
                'N/A'}</span>
            </div>
            
            <div className="detail-item">
              <label>Photo Capture:</label>
              <span>{record.photoMetadata?.captureTime ? formatDate(record.photoMetadata.captureTime) : 'N/A'}</span>
            </div>
            
            <div className="detail-item">
              <label>Live Capture:</label>
              <span>{record.photoMetadata?.isLiveCapture ? '✅ Yes' : '❌ No'}</span>
            </div>
            
            <div className="detail-item">
              <label>Created:</label>
              <span>{formatDate(record.createdAt)}</span>
            </div>
            
            <div className="detail-item">
              <label>Updated:</label>
              <span>{formatDate(record.updatedAt)}</span>
            </div>
            
            {record.notes && (
              <div className="detail-item full-width">
                <label>Employee Notes:</label>
                <span>{record.notes}</span>
              </div>
            )}
            
            {record.adminNotes && (
              <div className="detail-item full-width">
                <label>Admin Notes:</label>
                <span>{record.adminNotes}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceRecordCard;
